class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('#CartDrawer');
  }

  connectedCallback() {
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.setHeaderCartIconAccessibility();
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download') || link.closest('cart-drawer')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== routes.cart_url.replace(/\/$/, '')) return;
      event.preventDefault();
      this.open(link);
    }, options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]') || event.target.id === 'CartDrawer-Overlay') this.close();
      const clearButton = event.target.closest('[data-cart-clear]');
      if (clearButton) this.clearBag(clearButton);
    }, options);
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-bag-offer-form]');
      if (!form) return;
      event.preventDefault();
      this.addOffer(form);
    }, options);
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      const disclosure = this.querySelector('cart-disclosure-modal[open]');
      if (disclosure) disclosure.hide();
      else this.close();
    }, options);
    this.dialog.addEventListener('touchstart', (event) => {
      this.touchY = event.touches.length === 1 ? event.touches[0].clientY : null;
    }, { ...options, passive: true });
    this.dialog.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1 || this.touchY == null) return;
      const y = event.touches[0].clientY;
      this.containScroll(event, this.touchY - y);
      this.touchY = y;
    }, { ...options, passive: false });
    this.dialog.addEventListener('wheel', (event) => {
      if (!event.ctrlKey) this.containScroll(event, event.deltaY);
    }, { ...options, passive: false });
  }

  disconnectedCallback() {
    this.events?.abort();
    cancelAnimationFrame(this.openFrame);
    clearTimeout(this.closeTimer);
    if (this.dialog.open) this.finishClose(false);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    }, { signal: this.events.signal });
  }

  containScroll(event, delta) {
    if (!delta) return;
    // Long item lists and nested disclosure content may scroll; the shell and
    // background must never absorb their boundary gestures (including iOS bounce).
    for (let node = event.target; node && node !== this.dialog; node = node.parentElement) {
      if (node.scrollHeight <= node.clientHeight + 1 || !/auto|scroll/.test(getComputedStyle(node).overflowY)) continue;
      if (delta < 0 ? node.scrollTop > 0 : node.scrollTop + node.clientHeight < node.scrollHeight - 1) return;
    }
    if (event.cancelable) event.preventDefault();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.openFrame);
    if (!this.dialog.open) {
      this.pageScroll = { x: scrollX, y: scrollY };
      document.documentElement.style.setProperty('--obj-cart-scroll-top', `${-scrollY}px`);
      document.documentElement.style.setProperty('--obj-cart-scroll-left', `${-scrollX}px`);
      this.hadBodyLock = document.body.classList.contains('overflow-hidden');
      this.lenis = window.ObjectivScroll?.lenis;
      this.resumeLenis = this.lenis && !this.lenis.isStopped;
      this.lenis?.stop();
      document.body.classList.add('overflow-hidden');
      document.documentElement.classList.add('obj-cart-open');
      this.dialog.showModal();
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Establish the collapsed right-edge clip before the first painted reveal.
    this.querySelector('.drawer__inner').getBoundingClientRect();
    this.openFrame = requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });
    trapFocus(this.dialog, this.querySelector('.drawer__inner'));

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    if (!this.dialog.open) return;
    cancelAnimationFrame(this.openFrame);
    this.querySelectorAll('cart-disclosure-modal[open]').forEach((modal) => modal.hide(false));
    this.classList.remove('active');
    clearTimeout(this.closeTimer);
    // Shopify's CSS optimizer may rewrite 480ms as .48s; timers need milliseconds.
    const cssDuration = getComputedStyle(this).getPropertyValue('--obj-cart-duration').trim();
    const duration = (parseFloat(cssDuration) || 0) * (cssDuration.endsWith('ms') ? 1 : 1000);
    if (!duration) this.finishClose();
    else this.closeTimer = setTimeout(() => this.finishClose(), duration + 30);
  }

  finishClose(restoreFocus = true) {
    this.dialog.close();
    this.classList.remove('active');
    removeTrapFocus();
    document.documentElement.classList.remove('obj-cart-open');
    document.documentElement.style.removeProperty('--obj-cart-scroll-top');
    document.documentElement.style.removeProperty('--obj-cart-scroll-left');
    if (!this.hadBodyLock) document.body.classList.remove('overflow-hidden');
    if (this.pageScroll && !window.ObjectivScroll?.internal) {
      window.scrollTo({ left: this.pageScroll.x, top: this.pageScroll.y, behavior: 'instant' });
    }
    if (this.resumeLenis) this.lenis?.start();
    if (restoreFocus && this.activeElement?.isConnected) this.activeElement.focus({ preventScroll: true });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async clearBag(button) {
    const items = this.querySelector('cart-drawer-items');
    if (this.clearing || this.offerBusy || button.disabled || items.querySelector('.cart__items--disabled')) return;
    this.clearing = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    items.inert = true;
    const checkout = this.querySelector('#CartDrawer-Checkout');
    checkout.disabled = true;
    const errors = this.querySelector('#CartDrawer-CartErrors');
    errors.textContent = '';
    const updates = [...items.querySelectorAll('[data-quantity-line-key]')].map((input) =>
      items.createCartLinesUpdateEvent('remove', input.dataset.quantityVariantId, 0, input.dataset.quantityLineKey)
    ).filter(Boolean);
    let cleared = false;
    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const response = await fetch(`${routes.cart_url.replace(/\/$/, '')}/clear.js`, {
        ...fetchConfig(), body: JSON.stringify({ sections, sections_url: location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(state.description || state.errors || window.cartStrings.error);
      cleared = true;
      updates.forEach((update) => items.resolveCartLinesUpdate(update, state));
      // Section rendering can fail independently of a successful cart mutation.
      if (!sections.every((id) => state.sections?.[id])) {
        const refresh = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`);
        if (!refresh.ok) throw new Error(window.cartStrings.error);
        state.sections = await refresh.json();
      }
      if (!sections.every((id) => state.sections?.[id])) throw new Error(window.cartStrings.error);
      this.renderContents(state, { open: this.classList.contains('active') });
      try {
        Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state })).catch(() => {});
      } catch (_) { /* A subscriber failure must not undo a successful bag update. */ }
    } catch (error) {
      if (cleared) {
        // Refresh via GET rather than repeating a mutation that already succeeded.
        location.assign(routes.cart_url);
      } else {
        errors.textContent = error.message || window.cartStrings.error;
        items.dispatchCartErrorEvent(errors.textContent, 'SERVICE_UNAVAILABLE');
        updates.forEach((update) => update.reject(error));
      }
    } finally {
      this.clearing = false;
      if (items.isConnected) items.inert = false;
      if (checkout.isConnected) checkout.disabled = this.classList.contains('is-empty');
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  async addOffer(form) {
    const items = this.querySelector('cart-drawer-items');
    const button = form.querySelector('[type="submit"]');
    if (this.offerBusy || this.clearing || form.dataset.added || button.disabled ||
      items.querySelector('.cart__items--disabled') ||
      [...items.querySelectorAll('[data-quantity-line-key]')].some(input => input.value !== input.getAttribute('value'))) return;
    this.offerBusy = true;
    const idleLabel = button.textContent;
    const card = form.closest('[data-bag-offer]');
    const errorOutput = card.querySelector('[data-bag-offer-error]');
    const recovery = card.querySelector('[data-bag-offer-recovery]');
    const controls = [...this.querySelectorAll('#CartDrawer-Checkout,[data-cart-clear]')].map(node => ({node,disabled:node.disabled}));
    controls.forEach(({node}) => { node.disabled = true; });
    items.inert = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = form.dataset.addingLabel;
    errorOutput.textContent = '';
    recovery.hidden = true;
    const variantId = form.elements.id.value;
    const sections = this.getSectionsToRender().map(section => section.id);
    let mutated = false;
    try {
      const response = await fetch(form.action.replace(/(?:\.js)?$/, '.js'), {
        ...fetchConfig(),
        body: JSON.stringify({items:[{id:variantId,quantity:1}],sections,sections_url:location.pathname}),
      });
      // An OK response whose body cannot be read still added the product.
      const result = await response.json().catch(() => (response.ok ? {} : { status: response.status }));
      if (!response.ok || result.status || result.errors) throw new Error(result.description || form.dataset.errorMessage);
      mutated = true;
      form.dataset.added = 'true';
      // A successful add must never be repeated to recover missing section HTML.
      if (!sections.every(id => result.sections?.[id])) {
        const refresh = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`);
        if (!refresh.ok) throw new Error(form.dataset.refreshMessage);
        result.sections = await refresh.json();
      }
      if (!sections.every(id => result.sections?.[id])) throw new Error(form.dataset.refreshMessage);
      if (!this.isConnected) return;
      this.renderContents(result, {open:false});
      if (this.dialog.open && this.classList.contains('active')) {
        const addedInput = [...this.querySelectorAll('[data-quantity-variant-id]')].find(input => input.dataset.quantityVariantId === variantId);
        // Announce the added product without opening a phone's number keyboard.
        const target = addedInput?.closest('.cart-item')?.querySelector('.cart-item__name') || this.querySelector('#CartDrawer-Checkout');
        trapFocus(this.dialog, target);
      }
      try {
        Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {source:'cart-items',productVariantId:variantId,cartData:result})).catch(() => {});
      } catch (_) { /* Subscribers cannot undo a successful cart mutation. */ }
    } catch (error) {
      if (card.isConnected) {
        errorOutput.textContent = mutated ? form.dataset.refreshMessage : (error.message || form.dataset.errorMessage);
        recovery.hidden = !mutated;
      }
    } finally {
      this.offerBusy = false;
      if (items.isConnected) items.inert = false;
      controls.forEach(({node,disabled}) => { if (node.isConnected) node.disabled = disabled; });
      if (button.isConnected) {
        button.disabled = mutated;
        button.textContent = idleLabel;
        button.removeAttribute('aria-busy');
      }
    }
  }

  renderContents(parsedState, { open = true } = {}) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (open) this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // Repaint the bag from plain GETs. Used when a cart change succeeded but its
  // response could not be read or painted (for example a third-party script
  // wrapping fetch), so the bag never needs a page refresh to catch up.
  async refresh({ open = false } = {}) {
    const sections = this.getSectionsToRender().map((section) => section.id);
    const [sectionsResponse, cartResponse] = await Promise.all([
      fetch(`${routes.cart_url}?sections=${sections.join(',')}`),
      fetch(`${routes.cart_url}.js`),
    ]);
    if (!sectionsResponse.ok || !cartResponse.ok) throw new Error(window.cartStrings?.error);
    const state = await cartResponse.json();
    state.sections = await sectionsResponse.json();
    if (!sections.every((id) => state.sections?.[id])) throw new Error(window.cartStrings?.error);
    this.renderContents(state, { open });
    return state;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
